import classNames from 'classnames/bind';
import Button from 'mcs-toolkit/components/Button';
import SemanticMessage from 'mcs-toolkit/components/Message';
import * as R from 'ramda';
import React from 'react';
import {
    ButtonProps,
    MessageProps as SemanticMessageProps,
} from 'semantic-ui-react';

import CloseIcon from 'reactComponents/icons/close';
import {
    IconName,
    IconProps,
    IconUtils,
} from 'reactComponents/icons/utilities/iconUtils';

import styles from './Message.css';

const css = classNames.bind(styles);

type PredefinedStyle = {
    icon: IconName;
    className: string;
};

const PredefinedStyles: { [P in MessageType]: PredefinedStyle } = {
    error: {
        icon: 'info',
        className: 'error',
    },
    warning: {
        icon: 'info',
        className: 'warning',
    },
    info: {
        icon: 'info',
        className: 'info',
    },
    success: {
        icon: 'checkmarkRound',
        className: 'success',
    },
};

export type MessageType = 'warning' | 'error' | 'info' | 'success';

export type MessageProps = Pick<
    SemanticMessageProps,
    'className' | 'children' | 'hidden' | 'onDismiss'
> & {
    icon?: IconName | React.ComponentType<IconProps>;
    onClose?: () => void; // only call this method on cross click
    closable?: boolean; // hide self on cross click
    type?: MessageType;
    action?: Partial<ButtonProps>;
    messageDataTest?: string;
    closeDataTest?: string;
};

type State = {
    isHidden: boolean;
};

export class Message extends React.PureComponent<MessageProps, State> {
    static defaultProps = {
        messageDataTest: 'service-message',
        closeDataTest: 'close-message',
    };

    state: State = {
        isHidden: this.props.hidden || false,
    };

    _wrapIcon = R.unless(R.isNil, (icon) => (
        <div className={css('iconWrapper')}>{icon}</div>
    ));

    _getIcon = (icon) => {
        let type;

        if (typeof icon === 'string') {
            type = IconUtils.handleNameOrClass(icon);
            if (!type) {
                console.warn('unknown icon', icon);
                return;
            }
        }

        if (typeof icon === 'function') {
            type = icon;
        }

        if (type) {
            return React.createElement(type, {
                className: css('icon'),
                width: '23',
                height: '23',
            });
        }
        return icon;
    };

    _hideSelf = () => {
        this.setState({ isHidden: true });
    };

    _renderCross = () => {
        return (
            <div
                className={css('closeIconWrapper')}
                onClick={this.props.onClose || this._hideSelf}
                data-test={this.props.closeDataTest}
            >
                <CloseIcon
                    width="20"
                    height="20"
                    className={css('icon', 'closeIcon')}
                />
            </div>
        );
    };

    render = () => {
        if (this.state.isHidden) {
            return null;
        }

        let {
            children,
            className,
            onClose,
            closable,
            icon,
            type,
            action,
            messageDataTest,
            closeDataTest,
            ...otherMessageProps
        } = this.props;

        const predefinedStyle = type && PredefinedStyles[type];

        if (predefinedStyle && !icon) {
            icon = predefinedStyle.icon;
        }

        return (
            <SemanticMessage
                className={css(
                    'messageWrapper',
                    className,
                    predefinedStyle && predefinedStyle.className,
                )}
                data-test={messageDataTest}
                {...otherMessageProps}
            >
                {this._wrapIcon(this._getIcon(icon))}
                <SemanticMessage.Content className={css('content')}>
                    {children}
                </SemanticMessage.Content>
                {action && (
                    <Button
                        {...action}
                        className={css('actionButton', action.className)}
                    />
                )}
                {/*{(closable || onClose) && this._renderCross()}*/}
            </SemanticMessage>
        );
    };
}
